import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { finalize, forkJoin } from 'rxjs';
import { MenuItem, MessageService, ConfirmationService } from 'primeng/api';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TagModule } from 'primeng/tag';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { TooltipModule } from 'primeng/tooltip';
import { RippleModule } from 'primeng/ripple';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { CheckboxModule } from 'primeng/checkbox';
import { CrmClient, CrmClientEmployeeAssignment } from '../crm.model';
import { CrmService } from '../crm.service';
import { ToolbarService } from '../../services/toolbar.service';
import { PartyService } from '../../party/party.service';
import { Party } from '../../party/party.model';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageSearchPanelComponent } from '../../shared/page-layout/page-search-panel.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';

type AssignmentOption = { label: string; value: number; party?: Party; client?: CrmClient };

interface AssignmentClientRow {
  clientPartyId: number;
  clientName: string;
  crmClientId?: number;
  segment?: string;
  businessType?: string;
  primary?: CrmClientEmployeeAssignment;
  team: CrmClientEmployeeAssignment[];
  all: CrmClientEmployeeAssignment[];
  updatedAt?: Date;
  createdAt?: Date;
}

@Component({
  selector: 'app-accounts-list',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    DialogModule,
    ToastModule,
    ConfirmDialogModule,
    TagModule,
    SelectModule,
    MultiSelectModule,
    TooltipModule,
    RippleModule,
    BreadcrumbModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    CheckboxModule,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageSearchPanelComponent,
    PageDataPanelComponent
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './accounts-list.component.html',
  styleUrls: ['./accounts-list.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AccountsListComponent implements OnInit {
  private crmService = inject(CrmService);
  private partyService = inject(PartyService);
  private toolbarService = inject(ToolbarService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private fb = inject(FormBuilder);
  private router = inject(Router);

  assignments = signal<CrmClientEmployeeAssignment[]>([]);
  allAssignments = signal<CrmClientEmployeeAssignment[]>([]);
  clients = signal<CrmClient[]>([]);
  clientOptions = signal<AssignmentOption[]>([]);
  employeeOptions = signal<AssignmentOption[]>([]);
  loading = signal(false);
  saving = signal(false);
  assignmentDialog = signal(false);
  submitted = signal(false);
  lastUpdated = signal<Date | null>(null);

  searchText = signal('');
  clientPartyFilter = signal<number | null>(null);
  employeePartyFilter = signal<number | null>(null);
  ownerFilter = signal<boolean | null>(null);
  activeFilter = signal<boolean | null>(true);
  scorecards = signal<Record<string, any>>({});

  homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };
  breadcrumbItems: MenuItem[] = [
    { label: 'CRM', routerLink: '/crm/clients' },
    { label: 'Sales Assignments', routerLink: '/crm/assignments' }
  ];

  ownerOptions = [
    { label: 'Primary', value: true },
    { label: 'Team Member', value: false }
  ];

  activeOptions = [
    { label: 'Active', value: true },
    { label: 'Inactive', value: false }
  ];

  roleOptions = [
    { label: 'Team Member', value: 'TEAM_MEMBER' },
    { label: 'Operations', value: 'OPERATION' },
    { label: 'Account Support', value: 'ACCOUNT_SUPPORT' }
  ];

  assignmentForm = this.fb.group({
    clientPartyId: [null as number | null, Validators.required],
    primaryEmployeePartyId: [null as number | null, Validators.required],
    teamEmployeePartyIds: [[] as number[]],
    assignmentRole: ['TEAM_MEMBER'],
    active: [true]
  });

  rows = computed<AssignmentClientRow[]>(() => this.buildRows(this.assignments()));

  metrics = computed<PageMetric[]>(() => {
    const cards = this.scorecards();
    const activeAssignments = this.allAssignments().filter(row => row.active !== false);
    const primaryClientIds = new Set(activeAssignments.filter(row => row.primaryOwner).map(row => row.clientParty?.id?.entityId).filter(Boolean));
    const activeClientPartyIds = new Set(this.clients().map(client => client.linkedCompanyId).filter(Boolean));
    return [
      { label: 'Active assignments', value: Number(cards['active'] ?? activeAssignments.length) },
      { label: 'Clients with primary owner', value: Number(cards['primaryOwners'] ?? primaryClientIds.size), tone: 'success' },
      { label: 'Team members assigned', value: Number(cards['teamMembers'] ?? activeAssignments.filter(row => !row.primaryOwner).length), tone: 'info' },
      { label: 'Unassigned active clients', value: Math.max(activeClientPartyIds.size - primaryClientIds.size, 0), tone: 'warn' }
    ];
  });

  ngOnInit() {
    this.loadScorecards();
    this.loadOptionsAndAssignments();
  }

  loadScorecards() {
    const companyId = this.getCompanyId();
    if (!companyId) return;
    this.crmService.getAssignmentScorecards(companyId).subscribe(cards => this.scorecards.set(cards || {}));
  }

  get activeSearchCount(): number {
    return [
      this.searchText(),
      this.clientPartyFilter(),
      this.employeePartyFilter(),
      this.ownerFilter() !== null ? 'owner' : '',
      this.activeFilter() !== null ? 'active' : ''
    ].filter(Boolean).length;
  }

  get lastUpdatedText(): string {
    return this.lastUpdated() ? `Updated ${this.lastUpdated()!.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : 'Ready for assignment updates';
  }

  private getCompanyId(): number | null {
    return this.toolbarService.customer()?.company?.id ?? null;
  }

  loadOptionsAndAssignments() {
    const companyId = this.getCompanyId();
    if (!companyId) return;

    this.loading.set(true);
    forkJoin({
      clients: this.crmService.searchClients({ companyId, size: 500, sortBy: 'companyName', sortDir: 'asc' }),
      employees: this.partyService.getParties(companyId, ['Employee'], '', 0, 500),
      allAssignments: this.crmService.getClientAssignments(companyId)
    }).pipe(finalize(() => this.loading.set(false))).subscribe({
      next: ({ clients, employees, allAssignments }) => {
        const crmClients = clients.content || [];
        this.clients.set(crmClients);
        this.clientOptions.set(crmClients
          .filter(client => !!client.linkedCompanyId)
          .map(client => ({
            label: client.companyName,
            value: client.linkedCompanyId!,
            client
          })));
        this.employeeOptions.set((employees.content || []).map(party => ({
          label: party.entityName,
          value: party.id.entityId,
          party
        })));
        this.allAssignments.set(allAssignments || []);
        this.loadScorecards();
        this.loadAssignments();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'Load Failed', detail: 'Could not load assignment setup data.' })
    });
  }

  loadAssignments() {
    const companyId = this.getCompanyId();
    if (!companyId) return;
    this.loading.set(true);
    this.crmService.searchClientAssignments({
      companyId,
      searchText: this.searchText(),
      clientPartyId: this.clientPartyFilter() || undefined,
      employeePartyId: this.employeePartyFilter() || undefined,
      primaryOwner: this.ownerFilter(),
      active: this.activeFilter(),
      page: 0,
      size: 500,
      sortBy: 'createdAt',
      sortDir: 'desc'
    }).pipe(finalize(() => this.loading.set(false))).subscribe({
      next: response => {
        this.assignments.set(response.content || []);
        this.lastUpdated.set(new Date());
        this.loadScorecards();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'Search Failed', detail: 'Could not load sales assignments.' })
    });
  }

  applySearch() {
    this.loadAssignments();
  }

  clearSearch() {
    this.searchText.set('');
    this.clientPartyFilter.set(null);
    this.employeePartyFilter.set(null);
    this.ownerFilter.set(null);
    this.activeFilter.set(true);
    this.loadAssignments();
  }

  openNew() {
    this.assignmentForm.reset({
      clientPartyId: null,
      primaryEmployeePartyId: null,
      teamEmployeePartyIds: [],
      assignmentRole: 'TEAM_MEMBER',
      active: true
    });
    this.submitted.set(false);
    this.assignmentDialog.set(true);
  }

  editAssignment(row: AssignmentClientRow) {
    this.assignmentForm.reset({
      clientPartyId: row.clientPartyId,
      primaryEmployeePartyId: row.primary?.employeeParty?.id?.entityId ?? null,
      teamEmployeePartyIds: row.team.map(assignment => assignment.employeeParty?.id?.entityId).filter(Boolean) as number[],
      assignmentRole: row.team[0]?.assignmentRole || 'TEAM_MEMBER',
      active: row.all.some(assignment => assignment.active !== false)
    });
    this.submitted.set(false);
    this.assignmentDialog.set(true);
  }

  hideDialog() {
    this.assignmentDialog.set(false);
    this.submitted.set(false);
  }

  saveAssignment() {
    if (this.saving()) {
      return;
    }
    this.submitted.set(true);
    const companyId = this.getCompanyId();
    if (!companyId || this.assignmentForm.invalid) {
      this.assignmentForm.markAllAsTouched();
      this.messageService.add({ severity: 'warn', summary: 'Validation', detail: 'There are validation errors. Please review the highlighted fields.' });
      return;
    }

    const raw = this.assignmentForm.getRawValue();
    const teamIds = (raw.teamEmployeePartyIds || []).filter(id => id !== raw.primaryEmployeePartyId);
    this.saving.set(true);
    this.crmService.saveClientAssignments({
      companyId,
      clientPartyId: raw.clientPartyId!,
      primaryEmployeePartyId: raw.primaryEmployeePartyId!,
      employeePartyIds: [raw.primaryEmployeePartyId!, ...teamIds],
      teamEmployeePartyIds: teamIds,
      assignmentRole: raw.assignmentRole || 'TEAM_MEMBER',
      active: raw.active !== false,
      createdByCustomerId: this.toolbarService.customer()?.id ?? undefined,
      updatedByCustomerId: this.toolbarService.customer()?.id ?? undefined
    }).pipe(finalize(() => this.saving.set(false))).subscribe({
      next: response => {
        if (response.success) {
          this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Sales assignment updated', life: 3000 });
          this.hideDialog();
          this.loadOptionsAndAssignments();
        }
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'Save Failed', detail: 'Could not save sales assignment.' })
    });
  }

  deleteAssignment(row: AssignmentClientRow) {
    const assignmentIds = row.all.map(assignment => assignment.id).filter(Boolean) as number[];
    if (!assignmentIds.length) return;

    this.confirmationService.confirm({
      message: `Remove all sales assignments for <b>${row.clientName}</b>?`,
      header: 'Remove Assignments',
      icon: 'pi pi-exclamation-circle',
      acceptButtonStyleClass: 'p-button-danger p-button-text',
      rejectButtonStyleClass: 'p-button-text p-button-secondary',
      accept: () => {
        forkJoin(assignmentIds.map(id => this.crmService.deleteClientAssignment(id))).subscribe({
          next: () => {
            this.messageService.add({ severity: 'success', summary: 'Removed', detail: 'Assignments removed', life: 3000 });
            this.loadOptionsAndAssignments();
          }
        });
      }
    });
  }

  viewClient(row: AssignmentClientRow) {
    if (row.crmClientId) {
      this.router.navigate(['/crm/clients', row.crmClientId]);
    }
  }

  private buildRows(assignments: CrmClientEmployeeAssignment[]): AssignmentClientRow[] {
    const clientIds = new Set(assignments.map(assignment => assignment.clientParty?.id?.entityId).filter(Boolean));
    const source = this.allAssignments().filter(assignment => clientIds.has(assignment.clientParty?.id?.entityId));
    const grouped = new Map<number, CrmClientEmployeeAssignment[]>();
    source.forEach(assignment => {
      const clientPartyId = assignment.clientParty?.id?.entityId;
      if (!clientPartyId) return;
      grouped.set(clientPartyId, [...(grouped.get(clientPartyId) || []), assignment]);
    });

    return Array.from(grouped.entries()).map(([clientPartyId, rows]) => {
      const client = this.clients().find(item => item.linkedCompanyId === clientPartyId);
      const activeRows = rows.filter(row => row.active !== false);
      const primary = activeRows.find(row => row.primaryOwner) || rows.find(row => row.primaryOwner);
      const team = activeRows.filter(row => !row.primaryOwner);
      return {
        clientPartyId,
        clientName: client?.companyName || rows[0]?.clientParty?.entityName || `Client ${clientPartyId}`,
        crmClientId: client?.id,
        segment: client?.category,
        businessType: client?.businessType,
        primary,
        team,
        all: rows,
        updatedAt: rows.find(row => row.updatedAt)?.updatedAt,
        createdAt: rows.find(row => row.createdAt)?.createdAt
      };
    });
  }
}
